import { type InjectionKey, inject, provide, onMounted } from 'vue';
import type { StringWithPrefix } from '../utils/typescript-helpers';
import type { NloPlayer } from './useNloPlayer';

declare global {
	interface Window {
		dataLayer: Record<string, any>[];
	}
}

// List of possible event names that are allowed to be used in GA4 (source Twan)
type EventName =
	// Clicks
	| 'klik_button'
	| 'klik_link'
	| 'klik_menu'
	| 'klik_banner'
	| 'klik_checkbox'
	| 'klik_radiobutton'
	| 'klik_icon'
	| 'klik_dropdown'
	| 'klik_toggle'
	| 'klik_slider'
	| 'klik_formsubmit'
	| 'klik_tooltip'
	| 'klik_fileupload'
	// Non-interactions
	| 'info_message'
	| 'field_unfocus'
	// Views
	| 'view_banner'
	| 'view_element'
	// Video
	| 'video_played'
	| 'video_paused'
	| 'video_ended'
	// Virtual currency
	| 'spend_virtual_currency'
	| 'earn_virtual_currency'
	// Scanner events
	| 'scan_ticket';

export type GA4Params = {
	event_name?: EventName;
	interaction_content?: string;
	selection_value?: string;
	checkbox_value?: string;
	[key: string]: string | number | undefined;
};

export type GA4ECommerceBase = {
	item_name: 'Lotto-Online' | 'Eurojackpot-Online' | 'Miljoenenspel-Online' | string;
	/** The price in eurocents */
	price: number;
	quantity: number;
	item_variant?: string;
};

export type GA4ECommerceOthers = {
	item_brand: 'lotto' | 'eurojackpot' | 'krasloten' | 'luckyday';
	item_category: number | string;
	item_category2?: number | string;
	item_category3?: number | string;
	item_category4?: number | string;
	item_category5?: number | string;
} & GA4ECommerceBase;

export type GA4ECommerceSTL = {
	item_brand: 'staatsloterij';
	flow_type: 'losse loten' | 'abonnement' | StringWithPrefix<'KMA'>; // KMA -> Kennismakingsabonnement
	product_type: 'singleTicket' | 'street' | 'boulevard';
	product_fraction: 'yes' | 'no';
	game_type?: ('stl' | 'ojt' | 'kdt' | 'okt' | 'jul')[];
	xl?: 'yes' | 'no';
	draw_type?: 'Regulier' | 'Event trekking';
	draw_date?: string;
	upsell_type?: 'one-time' | 'basket';
} & GA4ECommerceBase;

export type GA4ECommerceMJS = {
	item_brand: 'miljoenenspel';
	flow_type: 'losse loten' | 'abonnement';
	number_of_draws: number;
	number_of_tickets: number;
	upsell_type?: 'cross-sell';
} & GA4ECommerceBase;

export type GA4ECommerceKD = {
	item_brand: 'krasloten';
	winning_chance?: 'Low' | 'Medium' | 'High';
} & GA4ECommerceBase;

export type GA4ECommerceItem = GA4ECommerceSTL | GA4ECommerceMJS | GA4ECommerceKD | GA4ECommerceOthers;

type GA4EcommerceObjectBase = {
	items: GA4ECommerceItem[];
	/** The value in eurocents */
	value?: number;
};

type GA4EcommerceInteraction = {
	/** begin_checkout is replaced by view_cart, remove if al verticals are converted **/
	event: 'view_item' | 'remove_from_cart' | 'add_to_cart' | 'begin_checkout' | 'view_cart';
	ecommerce: GA4EcommerceObjectBase;
};

type GA4EcommercePurchase = {
	event: 'ga4_purchase';
	ecommerce: GA4EcommerceObjectBase & {
		/** Unique transaction id, can be from the api or a generated UUID */
		transaction_id: string;
		coupon?: string;
	};
};

export type GA4ECommerceEvent = GA4EcommerceInteraction | GA4EcommercePurchase;

export type NloGtmPlugin = {
	/**
	 * Track the page view in GTM.
	 * @param pageName, the name of the current page.
	 */
	trackPage: (pageName: string) => void;

	/**
	 * Track an event in GTM. With event: 'trackEvent'
	 * @param params The params for GA4, interaction_context is automatically set via provideContextName or overwritten by the eventLabel param
	 * @param contextName The context name for the event, if not provided the one from provideContextName is used.
	 */
	trackEvent: (params: GA4Params, contextName?: string) => void;

	/**
	 * Track virtual page view. Use this for situations where you have different states that do not result in URL change.
	 * @param virtualURL
	 * @param virtualTitle
	 * @param pageType
	 */
	trackVirtualPageView: (virtualURL: string, virtualTitle: string, pageType?: string) => void;

	/**
	 * Track button press through Google analytics.
	 * @param buttonText, the text on the button
	 * @param contextName, the flow/page/feature this button is in. If no contextName is given, the one from the function provideContextName is used.
	 */
	trackButtonPress: (buttonText: string, contextName?: string) => void;

	/**
	 * Track link click through Google analytics.
	 * @param linkText, the visible text on the link
	 * @param contextName, the flow/page/feature this link is in. If no contextName is given, the one from the function provideContextName is used.
	 */
	trackLinkClick: (linkText: string, contextName?: string) => void;

	/**
	 * Track radio button press through Google analytics.
	 * @param label, the label of the radio button
	 * @param contextName, the flow/page/feature this button is in. If no contextName is given, the one from the function provideContextName is used.
	 */
	trackRadioButtonPress: (label: string, contextName?: string) => void;

	/**
	 * Track checkbox press through Google analytics.
	 * @param label, the label of the radio button
	 * @param checked, indicating if the user selected or de-selected the checkbox
	 * @param contextName, the flow/page/feature this button is in. If no contextName is given, the one from the function provideContextName is used.
	 */
	trackCheckboxPress: (label: string, checked: boolean, contextName?: string) => void;

	/**
	 * @deprecated This function is deprecated and will be removed in future releases.
	 * Please use {@link [useNloEcommerceTracking](./useNloEcommerceTracking.ts)} for the new implementation.
	 * Track ecommerce events through Google analytics.
	 */
	trackECommerceEvent: (event: GA4ECommerceEvent, contextName?: string) => void;

	trackUserSession: (player?: NloPlayer) => void;

	/**
	 * Provides a context name using provide/inject to your descendant components.
	 * This context name is used when calling trackButtonPress without any contextName.
	 * @param name
	 */
	provideContextName: (name: string) => void;
};

const contextNameInjectionKey: InjectionKey<string> = Symbol('nlo-gtm.contextNameInjectionKey');

/**
 * Composable to interact with GTM
 */
export default function useNloGtm(): NloGtmPlugin {
	/**
	 * Injection key used to provide context for tracking.
	 */
	const contextNameFromInjectionKey = inject(contextNameInjectionKey, undefined);

	onMounted(() => {
		getDataLayer();
	});

	function getDataLayer() {
		if (!window.dataLayer) {
			window.dataLayer = [];
		}

		return window.dataLayer;
	}

	function trackPage(pageName: string) {
		getDataLayer().push({
			pageType: pageName,
		});
	}

	function trackEvent(params: GA4Params, contextName?: string) {
		// For some reason, this is needed. If you have questions about it, please refer to the GTM people.
		getDataLayer().push({ event_params: null });

		const context = contextName || contextNameFromInjectionKey || '';

		getDataLayer().push({
			event: 'trackEvent',
			event_params: {
				interaction_context: context,
				...params,
			},
		});
	}

	function trackVirtualPageView(virtualURL: string, virtualTitle: string, pageType?: string) {
		const context = pageType || contextNameFromInjectionKey || '';

		getDataLayer().push({
			event: 'virtualPageview',
			virtualURL,
			virtualTitle,
			context,
		});
	}

	function trackButtonPress(buttonText: string, contextName?: string) {
		if (!buttonText) {
			// No button text means that this event isn't worth tracking.
			return;
		}

		trackEvent(
			{
				event_name: 'klik_button',
				interaction_content: buttonText,
			},
			contextName,
		);
	}

	function trackLinkClick(linkText: string, contextName?: string) {
		if (!linkText) {
			// No link text means that this event isn't worth tracking.
			return;
		}

		trackEvent(
			{
				event_name: 'klik_link',
				interaction_content: linkText,
			},
			contextName,
		);
	}

	function trackRadioButtonPress(label: string, contextName?: string) {
		if (!label) {
			// No radio button label means that this event isn't worth tracking.
			return;
		}

		trackEvent(
			{
				event_name: 'klik_radiobutton',
				interaction_content: label,
			},
			contextName,
		);
	}

	function trackCheckboxPress(label: string, checked: boolean, contextName?: string) {
		if (!label) {
			// No checkbox label means that this event isn't worth tracking.
			return;
		}

		trackEvent(
			{
				event_name: 'klik_checkbox',
				checkbox_value: checked ? 'on' : 'off',
				interaction_content: label,
			},
			contextName,
		);
	}

	/**
	 * @deprecated This function is deprecated and will be removed in future releases.
	 * Please refer useNloEcommerceTracking for the new implementation.
	 */
	const trackECommerceEvent = (event: GA4ECommerceEvent, contextName?: string) => {
		// Cents have to be converted to euros with 2 fraction digits and a . as separator
		const centFormatter = new Intl.NumberFormat('en-US', {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
			useGrouping: false,
		});

		const context = contextName || contextNameFromInjectionKey || '';

		const localEvent = {
			...event,
			interaction_context: context,
			ecommerce: {
				...event.ecommerce,
				currency: 'EUR',
				value: event.ecommerce.value !== undefined ? centFormatter.format(event.ecommerce.value / 100) : undefined,
				items: event.ecommerce.items.map((item) => ({
					...item,
					item_id: item.item_name,
					currency: 'EUR',
					price: centFormatter.format(item.price / 100),
				})),
			},
		};

		// For some reason, this is needed. If you have questions about it, please refer to the GTM people.
		getDataLayer().push({ ecommerce: null });

		getDataLayer().push(localEvent);
	};

	function trackUserSession(player?: NloPlayer) {
		getDataLayer().push({
			event: 'userDataReady',
			nyxId: player?.customerDetails.customerNumber?.toString() || '',
			loginStatus: player ? 'Wel ingelogd' : 'Niet ingelogd',
			playerState: player?.customerDetails.playerState || '',
			playerValidationState: player?.customerDetails.playerValidationState || '',
		});
	}

	function provideContextName(name: string) {
		provide(contextNameInjectionKey, name);
	}

	return {
		trackPage,
		trackEvent,
		trackVirtualPageView,
		trackLinkClick,
		trackButtonPress,
		provideContextName,
		trackRadioButtonPress,
		trackCheckboxPress,
		trackECommerceEvent,
		trackUserSession,
	};
}
